import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Card, StatusBadge, Timeline, Loading, EmptyState } from "../../components/common";
import { api } from "../../lib/api";
import { pkr, fmtDateTime, ORDER_STATUS, COMMISSION_STATUS } from "../../lib/format";

const Row = ({ k, v, mono }) => (
  <div className="flex justify-between gap-4 py-2 text-sm"><span className="text-slate-500">{k}</span><span className={`text-right ${mono ? "font-mono font-semibold" : ""}`}>{v || "—"}</span></div>
);

export default function MyOrderView() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/orders/mine/${id}`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [id]);
  if (err) return <EmptyState title="Order not found" message="This order does not exist or is not yours." />;
  if (!d) return <Loading />;
  const { order: o, commission: c } = d;
  return (
    <div className="space-y-6">
      <Link to="/app/orders" className="inline-flex items-center gap-1 text-sm text-slate-600"><ArrowLeft className="h-4 w-4" />My orders</Link>
      <div className="flex flex-wrap items-center gap-3"><h1 className="font-mono text-2xl font-bold" data-testid="order-view-no">{o.order_no}</h1><StatusBadge map={ORDER_STATUS} value={o.status} testid="order-view-status" /></div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="divide-y p-5">
            <h2 className="pb-2 font-heading text-lg font-semibold">Order</h2>
            <Row k="Deal" v={`${o.deal.name} (${o.deal.ref})`} />
            <Row k="Unit price (fixed at booking)" v={pkr(o.unit_price)} mono />
            <Row k="Quantity" v={o.quantity} mono />
            <Row k="Total" v={pkr(o.total_amount)} mono />
            <Row k="Sales ID" v={o.sales_id} mono />
            <Row k="Payment verified" v={o.payment_verified ? "Yes" : "Not yet"} />
            {o.tracking?.number && <Row k="Tracking" v={`${o.tracking.courier} ${o.tracking.number}`} />}
            <Row k="Booked on" v={fmtDateTime(o.created_at)} />
          </Card>
          <Card className="divide-y p-5">
            <h2 className="pb-2 font-heading text-lg font-semibold">Customer</h2>
            <Row k="Name" v={o.customer.name} /><Row k="Phone" v={o.customer.phone} /><Row k="Email" v={o.customer.email} /><Row k="Address" v={o.customer.address} /><Row k="Notes" v={o.notes} />
          </Card>
        </div>
        <div className="space-y-6">
          {c && (
            <Card className="p-5" data-testid="order-view-commission">
              <h2 className="font-heading text-lg font-semibold">Commission</h2>
              <p className="mt-3 font-mono text-2xl font-bold text-emerald-700">{pkr(c.amount)}</p>
              <div className="mt-2"><StatusBadge map={COMMISSION_STATUS} value={c.status} /></div>
              {c.eligible_at && c.status === "pending" && <p className="mt-2 text-xs text-slate-500">Eligible for review from {fmtDateTime(c.eligible_at)}</p>}
              {c.reason && <p className="mt-2 text-sm text-red-700">Reason: {c.reason}</p>}
            </Card>
          )}
          <Card className="p-5"><h2 className="mb-4 font-heading text-lg font-semibold">Status history</h2><Timeline items={[...o.history].reverse()} map={ORDER_STATUS} /></Card>
        </div>
      </div>
    </div>
  );
}
